import { usePatientCase } from "../usePatientCase";
import { useLabPanels } from "../useLabsPanels";
import {
  GenderValue,
  KahunCategories,
  KahunPanels,
  KahunPanelSubGroup,
  OrgansSystems,
  Relations,
  Values,
} from "shared";
import { useDiseasesSections } from "../hooks/useDiseasesSections";
import {
  featureToLines,
  getFindingLevel,
  getQFAndRefinementsLines,
  getQualifierByRelation,
  isNegate,
  isOfCategory,
  isOfRelation,
  valueName,
} from "fe-common/logic";
import { EXPOSURE_TO_FEATURE } from "../config/dataInputSectionConfiguration";
import { useMemo, useState } from "react";
import { buildLabSuggestionsPanels } from "../util/PanelsUtil";
import { DiseaseCategoryType, QualifiedFeature } from "fe-common/graphql";
import { useSuggestionsForPlan } from "../hooks/useSuggestionsForPlan";
import { useGetPanelAbnormalQf } from "../hooks/useGetPanelAbnormalQf";
import { PatientCaseGrouping } from "../../../context/diagnosticAgent";

enum DataGroups {
  PRESENT_ILLNESS = "Present illness",
  MEDICATION = "Medication",
  FAMILY_HISTORY = "Family history",
  HABITS = "habits",
  PHYSICAL_EXAMINATION = "Physical examination",
  DIFFERENTIAL_DIAGNOSIS = "Differential diagnosis",
  PLAN = "Plan",
}

interface DataSectionInterface {
  title: string;
  text: string;
  dataGroup?: DataGroups;
  isGroupTitle?: boolean;
  isParagraphTitle?: boolean;
  isChild?: boolean;
}

interface organSystemSignInterface {
  title?: string;
  organSystemIds: string[];
}

interface DataSectionInterface {
  title: string;
  text: string;
  dataGroup?: DataGroups;
  isGroupTitle?: boolean;
  isParagraphTitle?: boolean;
  isChild?: boolean;
}

interface GetFilteredQfsTextFunc {
  (
    qualifiedFeatures: QualifiedFeature[],
    filterFunc?: (qf: QualifiedFeature) => boolean,
  ): string;
}

const separator = ", ";

const singsByOrganSystemSigns: organSystemSignInterface[] = [
  { title: "Cardiovascular", organSystemIds: [OrgansSystems.CVD] },
  { title: "Respiratory", organSystemIds: [OrgansSystems.RESP] },
  { title: "Gastrointestinal", organSystemIds: [OrgansSystems.GI] },
  { title: "Neurological", organSystemIds: [OrgansSystems.NEU] },
  { title: "Musculoskeletal", organSystemIds: [OrgansSystems.MSK] },
  { title: "Skin", organSystemIds: [OrgansSystems.SKIN] },
  { title: "Genitourinary", organSystemIds: [OrgansSystems.GU] },
  { title: "HEENT", organSystemIds: [OrgansSystems.VS, OrgansSystems.AD] },
];

const getFilteredQfsText: GetFilteredQfsTextFunc = (
  qualifiedFeatures,
  filterFunc,
) =>
  qualifiedFeatures
    .filter(filterFunc ? filterFunc : () => true)
    .map((qf) => {
      let [title, qualifiersText] = getQFAndRefinementsLines(qf);
      title = title.split("|")[0];
      return qualifiersText ? `${title} (${qualifiersText})` : title;
    })
    .join(separator);

const initialDataGroups: Record<DataGroups, boolean> = {
  [DataGroups.PRESENT_ILLNESS]: true,
  [DataGroups.MEDICATION]: true,
  [DataGroups.FAMILY_HISTORY]: true,
  [DataGroups.HABITS]: true,
  [DataGroups.PHYSICAL_EXAMINATION]: true,
  [DataGroups.DIFFERENTIAL_DIAGNOSIS]: true,
  [DataGroups.PLAN]: true,
};

const useExportData = () => {
  const [activeDataGroups, setActiveDataGroups] = useState<
    Record<string, boolean>
  >(initialDataGroups);

  const { patientCase } = usePatientCase();
  const labPanels = useLabPanels(KahunPanelSubGroup.Labs, patientCase.workup);
  const { featuresSections } = useDiseasesSections();
  const { findingSuggestions, planIsLoading } = useSuggestionsForPlan();
  const {
    abnormalQf: abnormalVitalSigns,
    isAllNormal: isAllVitalSignsNormal,
    isAllCheck: isAllVitalSignsCheck,
  } = useGetPanelAbnormalQf(
    PatientCaseGrouping.CLINICAL_FEATURES,
    KahunPanels.VitalSigns,
  );

  const [
    chiefComplaintTitle,
    chiefComplaintQualifiersText,
  ] = getQFAndRefinementsLines(
    patientCase.main.length ? patientCase.main?.[0] : null,
    [Relations.DURATION],
  );

  const presentIllnessDuration = valueName(
    getQualifierByRelation(
      patientCase.main.length ? patientCase.main?.[0] : null,
      Relations.DURATION,
    ),
  );

  const genderText =
    patientCase.gender === GenderValue.MALE ? "Male" : "Female";

  const ageText = patientCase.age?.high
    ? `${patientCase.age.high} years old`
    : "";

  const chiefComplaintText =
    chiefComplaintTitle &&
    `presenting for evaluation of ${chiefComplaintTitle}`;

  const durationText = presentIllnessDuration
    ? `Onset of symptoms was ${presentIllnessDuration} ago.`
    : "";

  const qualifiersText =
    chiefComplaintQualifiersText &&
    `Patient describes ${chiefComplaintTitle} as ${chiefComplaintQualifiersText}.`;

  const presentIllnessText = `${genderText}, ${ageText}, ${chiefComplaintText}. ${durationText} ${qualifiersText}`;

  const complaints = patientCase.clinicalFeatures.concat(
    patientCase.reviewOfSystems,
  );

  const symptoms = getFilteredQfsText(
    complaints,
    (qf) => isOfCategory(KahunCategories.KAHUN_SYMPTOMS)(qf) && !qf.negate,
  );

  const symptomsText = symptoms
    ? `Patient has associated symptoms of ${symptoms}.`
    : `Patient has no associated symptoms`;

  const negateSymptomsText = getFilteredQfsText(
    complaints
      .filter(isOfCategory(KahunCategories.KAHUN_SYMPTOMS))
      .filter(isNegate),
  ).replace(/No /g, "");

  const backgroundTitles = getFilteredQfsText(patientCase.background, (qf) =>
    isOfCategory(KahunCategories.DISORDERS)(qf),
  );

  const backgroundText = backgroundTitles.length
    ? `Patient has medical history of: ${backgroundTitles}`
    : `With no pre-existing medical history.`;

  const medicationsTitles = getFilteredQfsText(
    patientCase.background,
    (qf) => qf.feature.id === EXPOSURE_TO_FEATURE,
  );

  const familyHistoryTitles = getFilteredQfsText(patientCase.background, (qf) =>
    isOfRelation(Relations.FAMILY_HISTORY)(qf),
  );

  const familyHistoryText =
    familyHistoryTitles || "No pertinent family history";

  const habitsTitles = getFilteredQfsText(patientCase.background, (qf) =>
    isOfCategory(KahunCategories.HABITS)(qf),
  );

  const vitalSignsTitles = getFilteredQfsText(complaints, (qf) =>
    isOfCategory(KahunCategories.VITAL_SIGNS)(qf),
  );

  const abNormalVitalSignsTitles = getFilteredQfsText(abnormalVitalSigns);

  const vitalSignsText = isAllVitalSignsNormal
    ? "All normal"
    : isAllVitalSignsCheck
    ? `All normal except ${abNormalVitalSignsTitles}`
    : vitalSignsTitles;

  const signs = complaints.filter(
    (qf) =>
      isOfCategory(KahunCategories.SIGNS_SYMPTOMS)(qf) ||
      isOfCategory(KahunCategories.SIGN)(qf),
  );

  const positiveSignsText = getFilteredQfsText(
    signs.filter((qf) => !isNegate(qf)),
  );

  const organSystemSignsDataSections: DataSectionInterface[] = singsByOrganSystemSigns.map(
    (item) => {
      const { title } = item;
      return {
        title,
        text: getFilteredQfsText(signs, (sign) =>
          item.organSystemIds.includes(sign.feature?.organSystem?.id),
        ),
        isChild: true,
        dataGroup: DataGroups.PHYSICAL_EXAMINATION,
      };
    },
  );

  const groupedOrgansSystemSum = singsByOrganSystemSigns.reduce(
    (sum, current) => {
      return {
        organSystemIds: sum.organSystemIds.concat(current.organSystemIds),
      };
    },
  );

  organSystemSignsDataSections.push({
    title: "Other",
    text: getFilteredQfsText(
      signs,
      (sign) =>
        !groupedOrgansSystemSum.organSystemIds.includes(
          sign.feature?.organSystem?.id,
        ),
    ),
    isChild: true,
    dataGroup: DataGroups.PHYSICAL_EXAMINATION,
  });

  const labTestsText = labPanels
    .filter(
      (panel) => panel.panel?.panelSubgroup?.id === KahunPanelSubGroup.Labs,
    )
    .map((labPanel) => {
      const isAllChecked =
        labPanel.panel.findings.length === labPanel.findings.length;
      const findingText = getFilteredQfsText(labPanel.findings, (finding) => {
        if (!isAllChecked) return true;
        return getFindingLevel(finding) !== Values.NORMAL;
      });
      return `${labPanel.panel?.title} - ${
        isAllChecked ? (findingText ? "All normal except: " : "All normal") : ""
      } ${findingText}`;
    })
    .join(". ");

  const ddClusters = featuresSections[0].elements.map((item) =>
    featureToLines(item.qf),
  );

  const assessmentAndPlanTexts = [
    `Patient presenting for evaluation of ${chiefComplaintTitle}, ${symptomsText}`,
    `\n${
      abNormalVitalSignsTitles
        ? "Vital signs revealed " + abNormalVitalSignsTitles
        : "Vital signs are normal"
    }.`,
    `\nPhysical examination is normal ${positiveSignsText &&
      "except for: " + positiveSignsText}.`,
    `${labTestsText ? `\nLaboratory test performed: ${labTestsText}` : ""}`,
    `${
      activeDataGroups[DataGroups.DIFFERENTIAL_DIAGNOSIS]
        ? `\nDiagnosis is most consistent with ${ddClusters[0]}.`
        : ""
    }`,
    `${
      activeDataGroups[DataGroups.DIFFERENTIAL_DIAGNOSIS] &&
      ddClusters.length > 1
        ? `\nDifferential diagnosis considered includes ${ddClusters[1]} ${
            ddClusters[2] ? `, ${ddClusters[2]}` : ""
          }. These were thought to be less likely given the history, exam, and workup.`
        : ""
    }`,
  ];
  const assessmentAndPlanText = assessmentAndPlanTexts.join("");

  const labsSuggestionPanels = useMemo(
    () =>
      buildLabSuggestionsPanels(
        findingSuggestions,
        DiseaseCategoryType.OutcomeTest,
      ),
    [findingSuggestions],
  );

  const labsSuggestion = labsSuggestionPanels.map((labPanel) =>
    labPanel.panel
      ? labPanel.panel.title
      : featureToLines(labPanel.suggestions[0].finding)[0].split("|")[0],
  );
  const labsSuggestionPanelsText = planIsLoading
    ? ""
    : labsSuggestion.slice(0, 3).join(separator);

  const dataSections: DataSectionInterface[] = [
    {
      title: "History of present illness",
      text: presentIllnessText,
      isParagraphTitle: true,
      dataGroup: DataGroups.PRESENT_ILLNESS,
    },
    { title: "", text: symptomsText },
    { title: "Patient denies", text: negateSymptomsText },
    { title: "Past medical history", text: backgroundText },
    {
      title: "Medications",
      text: medicationsTitles,
      dataGroup: DataGroups.MEDICATION,
    },
    {
      title: "Family history",
      text: familyHistoryText,
      dataGroup: DataGroups.FAMILY_HISTORY,
    },
    { title: "Habits", text: habitsTitles, dataGroup: DataGroups.HABITS },
    {
      title: "Physical Examination",
      text: "",
      isGroupTitle: true,
      isParagraphTitle: true,
      dataGroup: DataGroups.PHYSICAL_EXAMINATION,
    },
    {
      title: "Vital signs",
      text: vitalSignsText,
      dataGroup: DataGroups.PHYSICAL_EXAMINATION,
    },
    ...organSystemSignsDataSections,
    { title: "Laboratory tests", text: labTestsText, isParagraphTitle: true },
    {
      title: "Assessment and Plan",
      text: assessmentAndPlanText,
      isParagraphTitle: true,
    },
    {
      title: "Plan",
      text: labsSuggestionPanelsText,
      isParagraphTitle: true,
      dataGroup: DataGroups.PLAN,
    },
  ];

  return { dataSections, activeDataGroups, setActiveDataGroups };
};

export default useExportData;
